import { Button } from '@/components/ui/button'
import ChecklistItem from '@repo/datalayer/store/models/ChecklistItem'
import TemplateItem from '@repo/datalayer/store/models/TemplateItem'
import { captureException } from '@sentry/react'
import { Image, ImageUp, Upload } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

export const FileUploadButton = observer(function FileUploadButton({
  item,
  useIcon,
  onUpload,
}: {
  item: ChecklistItem | TemplateItem
  useIcon?: boolean
  onUpload: (filename: string, filetype: string) => void
}) {
  const [isUploading, setIsUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const ref = useRef<HTMLInputElement>(null)
  const { t } = useTranslation()
  const label = useIcon ? <ImageUp size={24} /> : t('checklists.addImage')

  return (
    <>
      <Button
        disabled={isUploading}
        onClick={async (e) => {
          e.preventDefault()

          ref.current?.click()
        }}
        size="sm"
        variant="secondary"
      >
        {isUploading ? `${progress}%` : label}
      </Button>
      <input
        onChange={async (e) => {
          const file = e.target.files?.[0]

          if (!file) return

          const data = await item.getUploadUrl(file.name)

          const formData = new FormData()
          formData.append('file', file)

          setIsUploading(true)
          uploadFile(file, data.url, (pct) => {
            console.log('upload progress', pct)
            setProgress(Math.round(pct))
          })
            .then(() => {
              console.log('upload complete')

              onUpload(data.filename, file.type)

              // clear the input
              ref.current!.value = ''
              // now we need to set the url on the item
            })
            .catch((e) => {
              console.error('upload failed', e)

              captureException(e)
              toast.success(t('checklists.uploadError'))

              // clear the input
              ref.current!.value = ''
            })
            .finally(() => {
              setIsUploading(false)
              setProgress(0)
            })
        }}
        type="file"
        accept="image/*,video/*"
        ref={ref}
        className="hidden"
      />
    </>
  )
})

export function uploadFile(
  file: File,
  presignedUploadUrl: string,
  onProgress: (pct: number) => void
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        const pct = e.loaded / e.total
        onProgress(pct * 100)
      }
    })
    xhr.upload.addEventListener('error', (e) => {
      reject(new Error('Upload failed: ' + e.toString()))
    })
    xhr.upload.addEventListener('abort', (e) => {
      reject(new Error('Upload aborted: ' + e.toString()))
    })
    xhr.addEventListener('load', (e) => {
      if (xhr.status === 200) {
        resolve()
      } else {
        reject(new Error('Upload failed ' + xhr.status))
      }
    })
    xhr.open('PUT', presignedUploadUrl, true)
    try {
      xhr.send(file)
    } catch (e) {
      const err = e as Error
      reject(new Error('Upload failed: ' + err.toString()))
    }
  })
}
